import { t, Instance, flow } from 'mobx-state-tree';
import { gedcomExportApi } from '@/api/gedcomExportWrapper';
import { WeTreeApiError } from '@/api/errors';
import type {
  GedcomExportBaselineResponse,
  GedcomExportFormat,
  GedcomExportMode,
  GedcomExportResponse
} from '@/types/gedcom-export';

/**
 * GedcomExportStore — sync full/delta GEDCOM download for owner/admin.
 */
export const GedcomExportStore = t
  .model('GedcomExportStore', {
    baselineExportId: t.maybeNull(t.string),
    baselineExportedAt: t.maybeNull(t.string),
    /** GEDCOM version the loaded baseline was written in */
    baselineFormat: t.maybeNull(t.string),
    exportBusy: t.optional(t.boolean, false),
    baselineLoading: t.optional(t.boolean, false),
    lastError: t.maybeNull(t.string)
  })
  .views((self) => ({
    get hasBaseline(): boolean {
      return Boolean(self.baselineExportId);
    },

    /**
     * A delta says what changed since a baseline, which only means something when both are
     * written in the same GEDCOM version.
     */
    hasBaselineFor(format: GedcomExportFormat): boolean {
      return Boolean(self.baselineExportId) && self.baselineFormat === format;
    }
  }))
  .actions((self) => ({
    clearError() {
      self.lastError = null;
    },

    clearState() {
      self.baselineExportId = null;
      self.baselineExportedAt = null;
      self.baselineFormat = null;
      self.exportBusy = false;
      self.baselineLoading = false;
      self.lastError = null;
    },

    loadBaseline: flow(function* (familyId: string, format: GedcomExportFormat = '5.5.1') {
      self.baselineLoading = true;
      self.lastError = null;
      try {
        const result: Awaited<ReturnType<typeof gedcomExportApi.getBaseline>> = yield gedcomExportApi.getBaseline(
          familyId,
          format
        );
        if (result.success) {
          const data = result.data as GedcomExportBaselineResponse;
          self.baselineExportId = data.exportId;
          self.baselineExportedAt = data.exportedAt;
          self.baselineFormat = data.format;
          return { success: true as const, data };
        }
        const err = result.error;
        if (err instanceof WeTreeApiError && err.status === 404) {
          self.baselineExportId = null;
          self.baselineExportedAt = null;
          self.baselineFormat = null;
          return { success: true as const, data: null };
        }
        const message = err instanceof WeTreeApiError ? err.getUserMessage() : 'Failed to load export baseline.';
        self.lastError = message;
        return { success: false as const, error: err };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load export baseline.';
        self.lastError = message;
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error(message)
        };
      } finally {
        self.baselineLoading = false;
      }
    }),

    exportGedcom: flow(function* (familyId: string, mode: GedcomExportMode, format: GedcomExportFormat = '5.5.1') {
      self.exportBusy = true;
      self.lastError = null;
      try {
        const result: Awaited<ReturnType<typeof gedcomExportApi.createExport>> = yield gedcomExportApi.createExport(
          familyId,
          { mode, format }
        );
        if (result.success) {
          const data = result.data as GedcomExportResponse;
          // A delta is measured against a baseline in the same GEDCOM version, so a full
          // export only becomes the baseline the store knows about for that version.
          if (mode === 'full') {
            self.baselineExportId = data.exportId;
            self.baselineExportedAt = data.createdAt;
            self.baselineFormat = data.format;
          }
          return { success: true as const, data };
        }
        const err = result.error;
        const message = err instanceof WeTreeApiError ? err.getUserMessage() : 'Failed to export GEDCOM.';
        self.lastError = message;
        return { success: false as const, error: err };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to export GEDCOM.';
        self.lastError = message;
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error(message)
        };
      } finally {
        self.exportBusy = false;
      }
    })
  }));

export type GedcomExportStoreType = Instance<typeof GedcomExportStore>;

export const gedcomExportStore = GedcomExportStore.create({
  baselineExportId: null,
  baselineExportedAt: null,
  baselineFormat: null,
  exportBusy: false,
  baselineLoading: false,
  lastError: null
});
